export interface PendingResignation {
    employeeId?: number;
    employeeName?: string;

    department?: number;
    departmentLabel?: string;

    designation?: number;
    designationLabel?: string;

    manager?: number;
    managerLabel?: string;

    hod_id?: number;
    hodLabel?: string;

    hrId?: number;
    hrLabel?: string;

    noticeDate?: string;
    resignationDate?: string;

    reason?: string;
}

export interface PendingDeleteResignation {
    awaitingName?: boolean;
}

export const startCreateResignationFlow = (
    setPendingResignation: (data: PendingResignation | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    setPendingResignation({});
    pushAIChat("Sure! Which employee is resigning?");
    speak("Which employee is resigning?");

    window.dispatchEvent(
        new CustomEvent("ai-suggest-options", {
            detail: { type: "employee-resignation" }
        })
    );
};

export const processPendingResignation = (
    text: string,
    pending: PendingResignation,
    setPendingResignation: (data: PendingResignation | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // 1️⃣ Employee (already selected via suggestion)
    if (!pending.employeeId) {
        pushAIChat("Please select an employee.");
        speak("Please select an employee.");
        return;
    }

    // 2️⃣ Notice Date
    if (!pending.noticeDate) {
        const cleanDate = text.trim();

        if (!cleanDate) {
            pushAIChat("Please provide a valid notice date.");
            speak("Please provide a valid notice date.");
            return;
        }

        setPendingResignation({ ...pending, noticeDate: cleanDate });
        pushAIChat("What is the resignation date?");
        speak("What is the resignation date?");
        return;
    }

    // 3️⃣ Resignation Date
    if (!pending.resignationDate) {
        const cleanDate = text.trim();

        if (!cleanDate) {
            pushAIChat("Please provide a valid resignation date.");
            speak("Please provide a valid resignation date.");
            return;
        }

        setPendingResignation({ ...pending, resignationDate: cleanDate });
        finalizeResignation(
            { ...pending, resignationDate: cleanDate },
            setPendingResignation,
            pushAIChat,
            speak
        );
    }
};


const finalizeResignation = (
    pending: PendingResignation,
    setPendingResignation: (data: PendingResignation | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    pushAIChat("Creating resignation now...");
    speak("Creating resignation now.");

    window.dispatchEvent(
        new CustomEvent("ai-create-resignation", {
            detail: {
                employeeId: pending.employeeId,
                department: pending.department,
                designation: pending.designation,
                manager: pending.manager,
                hod_id: pending.hod_id,
                hrId: pending.hrId,
                noticeDate: pending.noticeDate,
                resignationDate: pending.resignationDate,
                reason: pending.reason
            }
        })
    );

    setPendingResignation(null);
};

export const startDeleteResignationFlow = (
    setPendingDelete: (data: PendingDeleteResignation | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        pushAIChat(`Deleting resignation for "${nameFromIntent}"...`);
        speak(`Deleting resignation for ${nameFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-resignation", {
                detail: { name: nameFromIntent }
            })
        );
        return;
    }

    setPendingDelete({ awaitingName: true });
    pushAIChat("Which employee's resignation should be deleted?");
    speak("Which employee's resignation should be deleted?");
};

export const processPendingDeleteResignation = (
    text: string,
    pending: PendingDeleteResignation,
    setPendingDelete: (data: PendingDeleteResignation | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (!pending.awaitingName) return;

    const cleanName = text.replace(/[^a-zA-Z0-9 ]/g, "").trim();

    if (!cleanName) {
        pushAIChat("Please provide a valid employee name.");
        speak("Please provide a valid employee name.");
        return;
    }

    pushAIChat(`Deleting resignation for "${cleanName}"...`);
    speak(`Deleting resignation for ${cleanName}`);

    window.dispatchEvent(
        new CustomEvent("ai-delete-resignation", {
            detail: { name: cleanName }
        })
    );

    setPendingDelete(null);
};
